import React, { useState, useMemo, useEffect } from 'react';
import './App.css';
import './styles/globals.css';
import { registerServiceWorker } from './utils/registerServiceWorker';
import { PWAInstallPrompt } from './components/common/PWAInstallPrompt';
import { Header } from './components/sections/Header';
import { Hero } from './components/sections/Hero';
import { Features } from './components/sections/Features';
import { Spaces } from './components/sections/Spaces';
import { Pricing } from './components/sections/Pricing';
import { CTA } from './components/sections/CTA';
import { Footer } from './components/sections/Footer';
import { StickyBookButton } from './components/common/StickyBookButton';
import { EnquiryForm } from './components/forms/EnquiryForm';
import { ServicePage } from './components/sections/ServicePage';
import { ContactPage } from './components/pages/ContactPage';
import { CheckoutPage } from './components/pages/CheckoutPage';
import { PurchaseCompletePage } from './components/pages/PurchaseCompletePage';
import { UserDashboard } from './components/account/UserDashboard';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useBookingActions } from './hooks/useBookingActions';
import { VideoThumbnail } from './components/sections/VideoThumbnail';
import { SeatMap } from './seatmap/SeatMap';
import { generateSeatMapData } from './seatmap/seatmap.config';

function AppContent() {
  const [showContact, setShowContact] = useState(false);
  const [showServicePage, setShowServicePage] = useState(false);
  const [showCheckoutPage, setShowCheckoutPage] = useState(false);
  const [showPurchaseComplete, setShowPurchaseComplete] = useState(false);
  const [showSeatMap, setShowSeatMap] = useState(false);
  const [showAccountPage, setShowAccountPage] = useState(false);
  const [checkoutData, setCheckoutData] = useState<any>(null);
  const [transactionData, setTransactionData] = useState<any>(null);
  const { user, isLoading } = useAuth();
  const {
    handleEnquirySubmit,
    openEnquiryForm,
    closeEnquiryForm,
    showEnquiryForm,
  } = useBookingActions();

  const seatMapData = useMemo(() => generateSeatMapData(), []);

  // Check if we need to redirect to account page after login
  React.useEffect(() => {
    const redirectToAccount = localStorage.getItem('redirectToAccount');
    if (redirectToAccount === 'true' && user) {
      localStorage.removeItem('redirectToAccount');
      navigate('account'); // Ensure history state is set
      setShowAccountPage(true);
    }
  }, [user]);

  // Handle logout - reset to home when user becomes null
  React.useEffect(() => {
    if (!isLoading && !user) {
      console.log('User logged out - resetting to home page');
      // Reset all page states
      setShowContact(false);
      setShowServicePage(false);
      setShowCheckoutPage(false);
      setShowPurchaseComplete(false);
      setShowSeatMap(false);
      setShowAccountPage(false);
      setCheckoutData(null);
      setTransactionData(null);
    }
  }, [user, isLoading]);

  // Restore navigation state on refresh
  React.useEffect(() => {
    if (!isLoading) {
      const currentPage = window.history.state?.page;
      console.log('Restoring navigation state - Current page:', currentPage, 'User:', !!user);
      
      if (currentPage === 'account' && user) {
        setShowAccountPage(true);
      } else if (currentPage === 'seatmap') {
        setShowSeatMap(true);
      } else if (currentPage === 'contact') {
        setShowContact(true);
      } else if (currentPage === 'checkout') {
        setShowCheckoutPage(true);
        setShowSeatMap(true);
      } else if (currentPage === 'purchase-complete') {
        setShowPurchaseComplete(true);
      } else if (currentPage === 'service') {
        setShowServicePage(true);
      } else if (user && !currentPage) {
        // If user is logged in but no page in history, go to account
        navigate('account');
        setShowAccountPage(true);
      }
      // If no valid page or user not authenticated for account page, stay on home
    }
  }, [isLoading, user]);

  // Tag the initial history entry so back from seatmap returns to home
  React.useEffect(() => {
    if (!window.history.state?.page) {
      window.history.replaceState({ page: 'home' }, '');
    }
  }, []);

  // Handle browser back/forward button
  React.useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const page = event.state?.page ?? 'home';
      const data = event.state?.checkoutData ?? null;

      console.log('PopState triggered - navigating to page:', page, 'User:', !!user);
      
      // Reset all states first
      setShowContact(false);
      setShowServicePage(false);
      setShowSeatMap(false);
      setShowCheckoutPage(false);
      setShowPurchaseComplete(false);
      setShowAccountPage(false);
      
      // Then set the correct state based on page and user authentication
      if (page === 'account' && user) {
        setShowAccountPage(true);
      } else if (page === 'seatmap') {
        setShowSeatMap(true);
      } else if (page === 'contact') {
        setShowContact(true);
      } else if (page === 'checkout') {
        setShowCheckoutPage(true);
        setShowSeatMap(true); // Keep seatmap visible behind checkout
      } else if (page === 'purchase-complete') {
        setShowPurchaseComplete(true);
      } else if (page === 'service') {
        setShowServicePage(true);
      }
      // If page is 'home' or user is not authenticated, all states remain false (shows home)

      if (page === 'checkout' && data) {
        setCheckoutData(data);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [user]);

  const navigate = (page: string, state?: Record<string, any>) => {
    window.history.pushState({ page, ...state }, '');
  };

  const handleNavigateToContact = () => {
    navigate('contact');
    setShowContact(true);
  };

  const handleBackToHome = () => {
    navigate('home');
    setShowContact(false);
    setShowServicePage(false);
    setShowCheckoutPage(false);
    setShowPurchaseComplete(false);
    setShowSeatMap(false);
    setShowAccountPage(false);
  };

  const handleBackFromPurchaseComplete = () => {
    if (user) {
      // Logged in user - navigate to account dashboard
      navigate('account');
      setShowPurchaseComplete(false);
      setShowAccountPage(true);
    } else {
      // Not logged in - go to home
      handleBackToHome();
    }
  };

  const handleBookItem = (item: any) => {
    navigate('checkout', { checkoutData: item });
    setCheckoutData(item);
    setShowCheckoutPage(true);
  };

  const handleNavigateToCheckoutFromSeatMap = (data: any) => {
    navigate('checkout', { checkoutData: data });
    setCheckoutData(data);
    setShowCheckoutPage(true);
  };

  const handlePurchaseComplete = (data: any) => {
    navigate('purchase-complete');
    setTransactionData(data);
    setShowPurchaseComplete(true);
  };

  const handleNavigateToAccount = () => {
    navigate('account');
    setShowAccountPage(true);
  };

  // Show loading spinner while authentication is being restored
  if (isLoading) {
    return (
      <div className="App" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (showAccountPage) {
    return (
      <div className="App">
        <UserDashboard onBack={handleBackToHome} onNewBooking={() => { 
          console.log('Navigation to seatmap triggered from UserDashboard');
          // Ensure current state is properly set as 'account' before navigating
          if (!window.history.state || window.history.state.page !== 'account') {
            window.history.replaceState({ page: 'account' }, '');
          }
          navigate('seatmap'); 
          setShowSeatMap(true); 
          setShowAccountPage(false); 
        }} />
      </div>
    );
  }

  if (showPurchaseComplete) {
    return (
      <div className="App">
        <PurchaseCompletePage transactionData={transactionData} onBackToHome={handleBackFromPurchaseComplete} />
      </div>
    );
  }

  if (showSeatMap || showCheckoutPage) {
    return (
      <div className="App">
        <div style={{ display: showCheckoutPage ? 'none' : 'block' }}>
          <SeatMap
            onNavigateToCheckout={handleNavigateToCheckoutFromSeatMap}
            onBack={() => {
              console.log('SeatMap back button clicked');
              window.history.back();
            }}
          />
        </div>
        {showCheckoutPage && (
          <CheckoutPage
            onBack={() => setShowCheckoutPage(false)}
            checkoutData={checkoutData}
            onPurchaseComplete={handlePurchaseComplete}
          />
        )}
      </div>
    );
  }

  if (showServicePage) {
    return (
      <div className="App">
        <ServicePage onBack={handleBackToHome} onBookItem={handleBookItem} />
      </div>
    );
  }

  if (showContact) {
    return (
      <div className="App">
        <ContactPage 
          onBack={handleBackToHome}
          onNavigateToMemberships={() => {}}
          onOpenSeatMap={() => { navigate('seatmap'); setShowSeatMap(true); }}
          onLoginSuccess={handleNavigateToAccount}
        />
      </div>
    );
  }

  return (
    <div className="App">
      <Header onNavigateToContact={handleNavigateToContact} />
      <Hero onNavigateToContact={handleNavigateToContact} />
      <Features />
      <Spaces />
      <Pricing onNavigateToContact={handleNavigateToContact} />
      <VideoThumbnail />
      {/* <CTA /> */}
      <Footer />
      
      <StickyBookButton onClick={openEnquiryForm} />
      
      {showEnquiryForm && (
        <EnquiryForm 
          onSubmit={handleEnquirySubmit}
          onClose={closeEnquiryForm}
        />
      )}
    </div>
  );
}

function App() {
  useEffect(() => {
    // Register Service Worker for PWA functionality
    registerServiceWorker();
  }, []);

  return (
    <AuthProvider>
      <PWAInstallPrompt />
      <AppContent />
    </AuthProvider>
  );
}

export default App;